import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Cadrul comun al paginilor „Detalii” si „Încarcă poze” (B3): radacina, coloana si linkul inapoi.
 * Un singur loc, ca o reparatie de layout sa nu ajunga doar intr-una din ele (review Task 10).
 *
 * - Scroller propriu DOAR de la `md` (tiparul din AcasaPage): pe desktop shell-ul autentificat taie
 *   overflow-ul, deci pagina isi ia `min-h-0 flex-1 overflow-y-auto`. Sub `md` shell-ul curge si
 *   deruleaza documentul; un `overflow-y-auto` fara `md:` ar face din radacina un scroller cat tot
 *   continutul, iar o bara sticky s-ar ancora la el, nu la viewport (review Task 9).
 * - `pt-14` sub `md`: rezerva pentru hamburgerul flotant (fixed, 52x52 la 16,16), care altfel
 *   acopera linkul „Înapoi”.
 * - `footer` sta dupa coloana, in radacina: o bara sticky pusa acolo e in flux, deci nu acopera
 *   niciodata ultimul paragraf.
 * - `flex-1` FARA prefix: si sub `md` radacina umple zona de continut (shell-ul are `min-height:
 *   100dvh` pe mobil), iar coloana `flex-1` impinge footer-ul la baza ecranului cand continutul e
 *   scurt. Fara el, bara statea imediat sub continut (top≈270 la 360×640). Nu creeaza scroll:
 *   sub `md` radacina n-are `overflow`, deci deruleaza tot documentul.
 * - Continutul sta pe o suprafata (`bg-card`, contur `border-hairline`), nu direct pe fundalul cu
 *   model: glifele treceau peste ilustratii (final review I2). „Înapoi" si footer-ul raman in afara ei.
 * - `latime="lata"` (Detalii, 25.09): coloana `max-w-5xl`, ca imaginile sa creasca pe desktop; textul
 *   isi pastreaza singur o coloana de citire in pagina. „Încarcă poze" ramane ingusta: e un formular
 *   cu buton pe toata latimea.
 */
const PRACTICAL_LATIME = { ingusta: 'max-w-prose', lata: 'max-w-5xl' } as const;

export function PracticalPageShell({
  children,
  footer,
  latime = 'ingusta',
  lista = '/activitati-practice',
}: {
  children: ReactNode;
  footer?: ReactNode;
  latime?: keyof typeof PRACTICAL_LATIME;
  /** Unde duce „Înapoi": lista tipului (activitati practice sau provocari pentru scoli). */
  lista?: string;
}) {
  return (
    <div className="flex w-full flex-1 flex-col md:min-h-0 md:overflow-y-auto">
      <div className={`mx-auto w-full ${PRACTICAL_LATIME[latime]} flex-1 space-y-4 px-4 pb-4 pt-14 md:pt-4`}>
        <Link
          to={lista}
          className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Înapoi
        </Link>
        <div className="rounded-lg border border-hairline bg-card p-4 md:p-6">{children}</div>
      </div>
      {footer}
    </div>
  );
}
